/**
 * @fileoverview Hook de Actions Layer para permisos de llamada WhatsApp (Meta BSP).
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WhatsappPermissionsModel from '../../../../services/whatsappPermissionsService';
import { showtoast } from '../../../../util/toast';
import useCallProvider from '../../calls/useCallProvider';
import { isWhatsappCallsUiEnabledForProvider } from '../../calls/resolveCallProvider';
import useVoiceOutboundFrom from '../../voice/application/useVoiceOutboundFrom';
import {
  buildCallPermissionLineScope,
  getPermissionRequestErrorMessage,
  normalizeCallingPermission
} from './callPermissionHelpers';

export {
  buildCallPermissionLineScope,
  getPermissionRequestErrorMessage,
  normalizeCallingPermission
};

const getClientPhoneNumber = (client) => client?.phone_with_country_code || client?.phone;

export default function useCallPermissionActions() {
  const { t } = useTranslation('Inbox');
  const provider = useCallProvider();
  const callsEnabled = isWhatsappCallsUiEnabledForProvider(provider);
  const isVoiceProvider = provider === 'voice';
  const {
    selectedLine,
    enabledLines,
    loading: outboundLinesLoading,
    mustConfirmFrom,
    requiresFromSelection
  } = useVoiceOutboundFrom({ enabled: isVoiceProvider });
  const [checkingPermission, setCheckingPermission] = useState(null);
  const [requestingPermission, setRequestingPermission] = useState(false);

  /**
   * Default scope from the shared outbound selection.
   * Multi-number never falls back to "first line" — only the explicitly selected line.
   */
  const defaultLineScope = useMemo(() => {
    if (!isVoiceProvider) return {};

    // Single-number (legacy): do NOT scope the permission check to a line — the backend
    // resolves the legacy line from the `multi_number?` flag and the phone number. Only
    // multi-number (more than one SIP-ready line, requires explicit selection) sends the scope.
    if (!mustConfirmFrom) return {};

    // Multi-number never falls back to the "first line" — only the explicitly selected line.
    if (!selectedLine) return {};

    return buildCallPermissionLineScope({
      appId: selectedLine.appId,
      platformRetailerId: selectedLine.platformRetailerId
    });
  }, [isVoiceProvider, selectedLine, mustConfirmFrom]);

  const resolveLineScope = useCallback((overrideScope) => {
    if (overrideScope && typeof overrideScope === 'object') {
      return buildCallPermissionLineScope(overrideScope);
    }
    return defaultLineScope;
  }, [defaultLineScope]);

  const checkPermission = useCallback(async (client, overrideScope) => {
    if (!callsEnabled || !client) {
      return null;
    }

    try {
      setCheckingPermission(client.id);
      const phoneNumber = getClientPhoneNumber(client);
      if (!phoneNumber) return null;

      const lineScope = resolveLineScope(overrideScope);

      // Multi-number Voice: wait until dialable line(s) + appId are known; never fall back to legacy.
      // Single-number (legacy) sends no scope, so the appId gate must not apply.
      if (isVoiceProvider) {
        if (outboundLinesLoading) return null;
        if (mustConfirmFrom && !lineScope.appId) return null;
      }

      const response = await WhatsappPermissionsModel.getCallPermissionStatus(
        phoneNumber,
        lineScope
      );
      return normalizeCallingPermission(response);
    } catch {
      showtoast(t('wsCallSidebar.permissionCheckError'));
      return null;
    } finally {
      setCheckingPermission(null);
    }
  }, [
    callsEnabled,
    isVoiceProvider,
    mustConfirmFrom,
    outboundLinesLoading,
    resolveLineScope,
    t
  ]);

  const requestPermission = useCallback(async (client, {
    text,
    onSuccess,
    lineScope: overrideScope
  } = {}) => {
    if (!callsEnabled || !client) {
      return false;
    }

    try {
      setRequestingPermission(true);
      const phoneNumber = getClientPhoneNumber(client);
      if (!phoneNumber) return false;

      const lineScope = resolveLineScope(overrideScope);

      if (isVoiceProvider) {
        if (outboundLinesLoading) return false;
        if (mustConfirmFrom && !lineScope.appId) {
          showtoast(t('wsCallSidebar.permissionRequestError'));
          return false;
        }
      }

      await WhatsappPermissionsModel.requestCallPermission(phoneNumber, text, lineScope);
      showtoast(t('wsCallSidebar.permissionRequestSent'));
      if (typeof onSuccess === 'function') onSuccess();
      return true;
    } catch (error) {
      showtoast(getPermissionRequestErrorMessage(error) || t('wsCallSidebar.permissionRequestError'));
      return false;
    } finally {
      setRequestingPermission(false);
    }
  }, [
    callsEnabled,
    isVoiceProvider,
    mustConfirmFrom,
    outboundLinesLoading,
    resolveLineScope,
    t
  ]);

  return {
    checkPermission,
    requestPermission,
    checkingPermission,
    requestingPermission,
    // Multi-line permission UX is Voice-only (Fonema has no multi-line).
    mustConfirmFrom: isVoiceProvider && mustConfirmFrom,
    requiresFromSelection: isVoiceProvider && requiresFromSelection,
    enabledLines: isVoiceProvider ? enabledLines : [],
    selectedLine: isVoiceProvider ? selectedLine : null,
    outboundLinesLoading: isVoiceProvider && outboundLinesLoading,
    isVoiceProvider
  };
}
